import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { getInterviewFlow, nextStep, previousStep, stepPosition } from '../../utils/interviewFlow';
import styles from './InterviewBestYou.module.css';
import { Collapse } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CachedIcon from '@mui/icons-material/Cached';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import FeelLighterIcon from '../CustomIcons/FeelLighterIcon';
import WeightLiftingIcon from '../CustomIcons/WeightLiftingIcon';
import Footer from '../.shared/footer/footer';
import Header from '../.shared/header/header';

const PATH = '/interview/bestyou';

export default function InterviewBestYou() {
    const navigate = useNavigate();

    const { updateInterviewData, interviewData } = useInterview();
    const [ selectedGoal, setSelectedGoal ] = useState('none');

    // Read once per mount, so chrome and navigation cannot disagree if the
    // stored assignment changes while the page is open.
    const flow = useMemo(getInterviewFlow, []);

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.bestYouGoal) {
            setSelectedGoal(interviewData.bestYouGoal);
        }
        
        // Track when user reaches the best-you step
        trackPostHogEvent('interview_step_viewed', {
            interview_step: 'bestyou',
            step_number: stepPosition(flow, PATH),
            total_steps: flow.steps.length,
            interview_flow: flow.id,
            has_existing_data: !!interviewData.bestYouGoal
        });
    }, [interviewData.bestYouGoal, flow]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedGoal === 'none' && !recentlyChosen) return;

        const chosenGoal = recentlyChosen ? recentlyChosen : selectedGoal;

        setSelectedGoal(chosenGoal);

        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateInterviewData('bestYouGoal', chosenGoal);

            // Track best-you goal selection
            trackPostHogEvent('interview_bestyou_goal_selected', {
                selected_goal: chosenGoal,
                interview_step: 'bestyou',
                step_number: stepPosition(flow, PATH),
                interview_flow: flow.id
            });

            navigate(nextStep(flow, PATH) ?? '/interview/height-weight');
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(previousStep(flow, PATH) ?? '/');
    }

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header hideNavigation={flow.hideNavigation} />
                <ProgressBar step={stepPosition(flow, PATH)} totalSteps={flow.steps.length} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.bestYouContent}>
                        <h1 className={styles.title}>
                            <span>Imagine your best self.</span>
                            <span>Which path feels right for you?</span>
                        </h1>
                        <div className={styles.buttonsContainer}>

                            <button className={`${styles.interviewButton} ${selectedGoal === 'Feel lighter and more confident (lose 5-20 lbs)' ? styles.selected : ''}`} onClick={() => handleConfirm('Feel lighter and more confident (lose 5-20 lbs)')}>
                                <FeelLighterIcon sx={{ color: `${selectedGoal === 'Feel lighter and more confident (lose 5-20 lbs)' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Feel lighter and more confident (lose 5-20 lbs)</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedGoal === 'Boost energy and transform my health (lose 21-50 lbs)' ? styles.selected : ''}`} onClick={() => handleConfirm('Boost energy and transform my health (lose 21-50 lbs)')}>
                                <FlashOnIcon sx={{ color: `${selectedGoal === 'Boost energy and transform my health (lose 21-50 lbs)' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Boost energy and transform my health (lose 21-50 lbs)</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedGoal === 'Make a life-changing transformation (lose over 50 lbs)' ? styles.selected : ''}`} onClick={() => handleConfirm('Make a life-changing transformation (lose over 50 lbs)')}>
                                <CachedIcon sx={{ color: `${selectedGoal === 'Make a life-changing transformation (lose over 50 lbs)' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Make a life-changing transformation (lose over 50 lbs)</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedGoal === 'Stay strong and maintain my current weight' ? styles.selected : ''}`} onClick={() => handleConfirm('Stay strong and maintain my current weight')}>
                                <WeightLiftingIcon sx={{ color: `${selectedGoal === 'Stay strong and maintain my current weight' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Stay strong and maintain my current weight</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedGoal === "I’m still exploring my goals" ? styles.selected : ''}`} onClick={() => handleConfirm("I’m still exploring my goals")}>
                                <SearchIcon sx={{ color: `${selectedGoal === "I’m still exploring my goals" ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>I’m still exploring my goals</span>
                            </button>

                            <div className={styles.confirmationButtons}>
                                <button 
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button 
                                    className={`${styles.primaryButton} ${selectedGoal === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    Continue
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
